import React, { useEffect, useState } from "react";
import { Button, Empty, message, Modal, Radio, Spin } from "antd";
import {
  PrinterOutlined,
  SyncOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import { svg } from "../../../../../Assets/svg";

interface SelectPrinterModalProps {
  visible: boolean;
  setIsModalVisible: (visible: boolean) => void;
  dymo: any;
  onSendPrint: (selectedPrinterName: string) => Promise<void>;
  printLoader: boolean;
}

const SelectPrinterModal: React.FC<SelectPrinterModalProps> = ({
  visible,
  setIsModalVisible,
  dymo,
  onSendPrint,
  printLoader,
}) => {
  const [loadingPrinters, setLoadingPrinters] = useState<boolean>(true);
  const [printersList, setPrintersList] = useState<any[]>([]);
  const [selectedPrinter, setSelectedPrinter] = useState<string>("");

  const fetchPrinters = async () => {
    try {
      setLoadingPrinters(true);
      const printers: any = await dymo.getPrinters();
      if (printers?.success && printers?.data) {
        const list = printers.data;
        setPrintersList(list);
        if (list.length > 0) {
          const connected = list.find((p: any) => p.connected) || list[0];
          setSelectedPrinter((prev) => {
            if (prev && list.some((p: any) => p.name === prev)) {
              return prev;
            }
            return connected?.name || "";
          });
        } else {
          setSelectedPrinter("");
        }
      } else {
        setPrintersList([]);
        setSelectedPrinter("");
      }
    } catch (error) {
      console.error("Error fetching printers:", error);
      setPrintersList([]);
      setSelectedPrinter("");
    } finally {
      setLoadingPrinters(false);
    }
  };

  useEffect(() => {
    if (visible) {
      fetchPrinters();
    }
  }, [visible]);

  const onClose = () => {
    if (!printLoader) {
      setIsModalVisible(false);
    }
  };

  const handleSend = async () => {
    if (!selectedPrinter) {
      message.error("Please select a printer to print.");
      return;
    }
    await onSendPrint(selectedPrinter);
    onClose();
  };

  return (
    <Modal
      title={
        <div className="d-flex align-items-center gap-2 fs-5 fw-bold text-dark">
          <PrinterOutlined className="text-primary fs-4" />
          Select Printer
        </div>
      }
      open={visible}
      visible={visible}
      onCancel={onClose}
      width={"36rem"}
      closeIcon={svg.closeIcon}
      centered
      footer={
        <div className="d-flex justify-content-end gap-3 pt-2">
          <Button
            size="large"
            onClick={onClose}
            disabled={printLoader}
            className="px-4"
          >
            Cancel
          </Button>
          <Button
            type="primary"
            size="large"
            icon={<PrinterOutlined />}
            onClick={handleSend}
            loading={printLoader}
            disabled={!selectedPrinter || loadingPrinters || printersList.length === 0}
            className="px-4"
          >
            Send Print
          </Button>
        </div>
      }
      className="commonModalClass"
    >
      <div className="py-2">
        {loadingPrinters ? (
          <div className="d-flex flex-column justify-content-center align-items-center py-5">
            <Spin size="large" />
            <div className="mt-3 text-muted fw-medium fs-6">
              Searching for connected DYMO printers...
            </div>
          </div>
        ) : printersList.length === 0 ? (
          <div className="py-4 text-center">
            <Empty
              description="No DYMO printers detected. Make sure DYMO Connect is running."
              className="mb-3"
            />
            <Button
              type="primary"
              ghost
              icon={<SyncOutlined spin={loadingPrinters} />}
              onClick={fetchPrinters}
            >
              Refresh Printer Search
            </Button>
          </div>
        ) : (
          <div>
            <div className="text-secondary mb-3 fs-6">
              Select the DYMO printer you want to send the label print job to:
            </div>
            <Radio.Group
              onChange={(e) => setSelectedPrinter(e.target.value)}
              value={selectedPrinter}
              className="w-100"
            >
              <div className="d-flex flex-column gap-3">
                {printersList.map((printer: any, index: number) => {
                  const isSelected = selectedPrinter === printer.name;
                  return (
                    <div
                      key={printer.name || index}
                      onClick={() => setSelectedPrinter(printer.name)}
                      className={`p-3 rounded-3 border transition-all cursor-pointer d-flex align-items-center justify-content-between ${
                        isSelected
                          ? "border-primary bg-light-primary shadow-sm"
                          : "border-gray-200 bg-white"
                      }`}
                      style={{
                        borderColor: isSelected ? "#1890ff" : "#d9d9d9",
                        backgroundColor: isSelected ? "#f0f7ff" : "#ffffff",
                        cursor: "pointer",
                      }}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <Radio value={printer.name} />
                        <div
                          className="p-2 rounded-circle border d-flex align-items-center justify-content-center"
                          style={{
                            backgroundColor: isSelected ? "#f8f1f1ff" : "#fafafa",
                            borderColor: isSelected ? "#000000" : "#d9d9d9",
                          }}
                        >
                          <PrinterOutlined
                            style={{
                              fontSize: "20px",
                              color: isSelected ? "#000000" : "#595959",
                            }}
                          />
                        </div>
                        <div>
                          <div className="fw-bold text-dark fs-6">
                            {printer.name}
                          </div>
                          {printer.modelName &&
                            printer.modelName !== printer.name && (
                              <div className="text-muted fs-7">
                                {printer.modelName}
                              </div>
                            )}
                        </div>
                      </div>
                      <div>
                        {printer.connected ? (
                          <span
                            className="px-2 py-1 rounded-pill fs-7 fw-medium d-inline-flex align-items-center gap-1"
                            style={{
                              color: "#52c41a",
                              backgroundColor: "#f6ffed",
                              border: "1px solid #b7eb8f",
                            }}
                          >
                            <CheckCircleFilled style={{ fontSize: "12px" }} />{" "}
                            Connected
                          </span>
                        ) : (
                          <span
                            className="px-2 py-1 rounded-pill fs-7 fw-medium"
                            style={{
                              color: "#8c8c8c",
                              backgroundColor: "#fafafa",
                              border: "1px solid #d9d9d9",
                            }}
                          >
                            Disconnected
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Radio.Group>
            <div className="d-flex justify-content-end align-items-center mt-3">
              <Button
                type="text"
                size="small"
                icon={<SyncOutlined spin={loadingPrinters} />}
                onClick={fetchPrinters}
                className="text-primary"
              >
                Refresh List
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SelectPrinterModal;
